// Split Self (owner approved the concept 2026-11-02: 'Make them all'): glowing, semi-transparent energy copies of Aren run
// beside him and shoot light bolts. Render-only: reads sim.splits / sim.splitShots and the 'split' / 'splitShot' /
// 'splitPop' / 'splitEnd' events; decides nothing about gameplay.
//  - copies: pooled clones of the authored hero (same GLB, same clips) with every material swapped for one energy shader in
//    the hero's resonance colour (teal in base form, the Surge aura colour while surged): a see-through glowing body with
//    rising energy bands, a hard bright cel rim and a white flash as it splits off. They run with the walk/run clips and
//    throw each bolt with the approved Spirit Bolt arm thrust (bolt_r / bolt_l), alternating hands.
//  - distinct from Mirage Lure's still, pale-blue ghost decoy: solid-reading energy bodies that move and attack
//  - bolts: small teal energy darts (streak card with a white head + halo) leaving the copy's hand
//  - split-off burst, fading after-image motes while they run, a dissolve when they expire, a soft pool of light under each
// No per-frame allocations (the clones load once, lazily, the first time the skill is owned).
import * as THREE from 'three/webgpu';
import { abs, dot, texture, float, mix, oneMinus, normalView, positionViewDirection, positionWorld, pow, smoothstep, time, uniform, uv, vec3 } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import type { Fx } from './fx';
import { softDotTexture } from './fx';
import type { GroundGlow } from './groundGlow';
import { GltfHero } from './heroGltf';
import type { RenderStyle } from './toon';

const COPIES = 6;               // drawn copies (most a build can have: Mirror Legion 5 + Echo Chamber)
const SHOTS = 96;               // sim bolt pool size
const BASE_COL = 0x30eccf;      // resonance teal (base form)
const EASE_T = 0.1;             // a fresh bolt eases from the hand onto its sim path over this (s)
const FADE_IN = 0.3, FADE_OUT = 0.35;

type ShotEv = Extract<SimEvent, { t: 'splitShot' }>;
const mkU = () => uniform(0);
type U = ReturnType<typeof mkU>;
interface Look { g: GltfHero; alpha: U; flash: U; id: number; age: number; out: number; x: number; z: number; yaw: number; side: number; trail: number }
interface Rec { ox: number; oy: number; oz: number; age: number }

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3();
const _h = new THREE.Vector3();
const _d = new THREE.Vector3(), _dl = new THREE.Vector3(), _side = new THREE.Vector3(), _n = new THREE.Vector3();

export class SplitSelfFx {
  /** QA counters */
  summons = 0; shots = 0; loaded = 0;
  private looks: Look[] = []; private loading = false;
  private col = uniform(new THREE.Color(BASE_COL));
  private streak: THREE.InstancedMesh; private halo: THREE.InstancedMesh;
  private recs = new Map<number, Rec>();
  private hex = BASE_COL;

  constructor(private scene: THREE.Scene, private style: RenderStyle, private file: () => string, private fx: Fx, private groundAt: (x: number, z: number) => number) {
    // bolt: a dart card, x from -1 (tail) to 0 (head); teal body, white head, darker rim so it reads on grass
    const g = new THREE.PlaneGeometry(1, 1); g.translate(-0.5, 0, 0);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const u = uv().x, across = abs(uv().y.sub(0.5)).mul(2);
    const cap = u.sub(0.7).div(0.3).max(0), head = float(1).sub(cap.mul(cap)).max(0).sqrt();
    const w = smoothstep(0.0, 0.7, u).mul(0.85).add(0.15).min(head);
    const shape = smoothstep(w, w.mul(0.85), across), inner = smoothstep(w.mul(0.8), w.mul(0.6), across);
    const core = smoothstep(w.mul(0.5), w.mul(0.2), across).mul(smoothstep(0.35, 0.85, u));
    m.colorNode = mix(mix(this.col.mul(0.45), this.col.mul(1.25), inner), vec3(1.3, 1.35, 1.35), core);
    m.opacityNode = shape.mul(smoothstep(0.0, 0.45, u).mul(0.75).add(0.25));
    this.streak = new THREE.InstancedMesh(g, m, SHOTS); this.streak.count = 0; this.streak.frustumCulled = false; this.streak.renderOrder = 5;
    const hm = new THREE.MeshBasicNodeMaterial({ map: softDotTexture(), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
    hm.colorNode = this.col.mul(texture(hm.map!).r.mul(0.6)); // the soft dot shape (a colorNode replaces the map)
    this.halo = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), hm, SHOTS); this.halo.count = 0; this.halo.frustumCulled = false; this.halo.renderOrder = 4;
    scene.add(this.streak, this.halo);
  }

  /** the energy-copy material for one clone (shared colour, its own alpha and flash) */
  private energy(alpha: Look['alpha'], flash: Look['flash']): THREE.MeshBasicNodeMaterial {
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.FrontSide, fog: false });
    const facing = abs(dot(normalView, positionViewDirection)), fres = pow(oneMinus(facing), float(1.7));
    const bands = positionWorld.y.mul(8).sub(time.mul(5)).sin().mul(0.5).add(0.5).pow(10);          // energy rising through the body
    const shimmer = positionWorld.x.add(positionWorld.z).mul(13).add(time.mul(9)).sin().mul(0.5).add(0.5).pow(14).mul(0.5);
    const rim = smoothstep(0.34, 0.44, fres);                                                        // hard cel rim
    const body = this.col.mul(float(0.2).add(fres.mul(0.55)).add(bands.mul(0.9)).add(shimmer.mul(0.35)));  // deep teal core so it reads on bright grass
    const rimC = mix(this.col, vec3(1, 1, 1), 0.45).mul(1.8);
    m.colorNode = mix(body, rimC, rim).add(vec3(1, 1, 1).mul(flash.mul(1.4)));
    m.opacityNode = float(0.62).add(fres.mul(0.25)).add(bands.mul(0.2)).add(rim.mul(0.45)).add(flash.mul(0.4)).min(0.95).mul(alpha);
    return m;
  }

  private load(): void {
    this.loading = true;
    const url = `${import.meta.env.BASE_URL}models/${this.file() || 'aren.glb'}`;
    for (let i = 0; i < COPIES; i++) {
      const g = new GltfHero(this.style);
      void g.load(url).then(() => {
        const look: Look = { g, alpha: mkU(), flash: mkU(), id: 0, age: 0, out: -1, x: 0, z: 0, yaw: 0, side: 0, trail: 0 };
        const mat = this.energy(look.alpha, look.flash);
        g.root.traverse((o) => {
          const mm = o as THREE.Mesh; if (!mm.isMesh) return;
          if (g.outlineMeshes.includes(mm)) { mm.visible = false; return; }
          mm.material = mat; mm.castShadow = false; mm.renderOrder = 4;
        });
        g.root.visible = false; this.scene.add(g.root); this.looks.push(look); this.loaded++;
      }).catch(() => { /* particles only */ });
    }
  }

  /** 'split' event: copies formed (their bursts play when each new copy is first drawn) */
  summon(): void { this.summons++; }

  /** 'splitShot': remember where the copy's hand is so the bolt starts there; arm thrust; muzzle spark */
  shot(ev: ShotEv): void {
    this.shots++;
    const lk = this.looks.find((l) => l.id === ev.copy && l.out < 0);
    let hx = ev.x, hy = this.groundAt(ev.x, ev.z) + 1.25, hz = ev.z;
    if (lk && lk.g.root.visible) {
      const side = lk.side++ % 2 ? 'L' : 'R';
      if (lk.g.allows(side === 'R' ? 'bolt_r' : 'bolt_l')) lk.g.playAction(side === 'R' ? 'bolt_r' : 'bolt_l', Math.atan2(ev.dx, ev.dz));
      lk.g.handWorld(side, _h); hx = _h.x + ev.dx * 0.15; hy = _h.y; hz = _h.z + ev.dz * 0.15;
    }
    this.recs.set(ev.id, { ox: hx - ev.x, oy: hy, oz: hz - ev.z, age: 0 });
    this.fx.stars.add(hx, hy, hz, 0.5, 0.05, 0xe8fffa, 2.2);
  }
  /** 'splitPop': the bolt is gone (a tiny teal pop where it struck) */
  pop(ev: Extract<SimEvent, { t: 'splitPop' }>): void {
    const r = this.recs.get(ev.id); this.recs.delete(ev.id);
    if (!ev.hit) return;
    const y = r ? r.oy : this.groundAt(ev.x, ev.z) + 1.2;
    this.fx.stars.add(ev.x, y, ev.z, 0.8, 0.08, this.hex, 2.2);
    for (let k = 0; k < 3; k++) { const a = Math.random() * 6.283; this.fx.particles.emitDir(ev.x, y, ev.z, Math.cos(a) * 3, 1 + Math.random() * 2, Math.sin(a) * 3, this.hex, 0.14, 0.18, 2.2); }
  }

  /** Per frame. tint = the hero's resonance colour this frame (null = base teal). */
  update(dt: number, sim: Sim, cam: THREE.Camera, tint: number | null): void {
    if (!this.loading && (sim.wlv('split') > 0 || sim.splits.some((c) => c.alive))) this.load();
    this.hex = tint ?? BASE_COL; (this.col.value as THREE.Color).setHex(this.hex);
    // copies: bind each live sim copy to a look; looks whose copy is gone dissolve where it was
    for (const lk of this.looks) if (lk.id && lk.out < 0 && !sim.splits.some((c) => c.alive && c.id === lk.id)) { lk.out = FADE_OUT; this.dissolve(lk); }
    for (const c of sim.splits) {
      if (!c.alive) continue;
      let lk = this.looks.find((l) => l.id === c.id);
      if (!lk) { lk = this.looks.find((l) => l.out < 0 && !l.id) ?? this.looks.find((l) => l.out === 0); if (!lk) continue; lk.id = c.id; lk.age = 0; lk.out = -1; lk.yaw = Math.atan2(c.faceX, c.faceZ); this.burst(c.x, c.z); }
      lk.x = c.x; lk.z = c.z; lk.age += dt;
      let dy = Math.atan2(c.faceX, c.faceZ) - lk.yaw; dy = Math.atan2(Math.sin(dy), Math.cos(dy)); lk.yaw += dy * Math.min(1, dt * 14);
      const g = lk.g, sp = Math.hypot(c.vx, c.vz), gy = this.groundAt(c.x, c.z);
      g.root.visible = true; g.root.position.set(c.x, gy, c.z); g.root.rotation.set(0, lk.yaw, 0);
      lk.alpha.value = Math.min(1, lk.age / FADE_IN) * (Number.isFinite(c.t) ? Math.min(1, c.t / 0.25) : 1);
      lk.flash.value = Math.max(0, 1 - lk.age / 0.35);
      g.update(dt, { speed: sp, firing: false, charging: false, transformed: false, surgeTier: 0, hurt: false, dead: false, faceYaw: lk.yaw, moveYaw: Math.atan2(c.vx, c.vz), turn: dy });
      // after-image motes while running; a faint sparkle while standing
      lk.trail += dt * (sp > 3 ? 30 : 6);
      for (; lk.trail >= 1; lk.trail--) this.fx.particles.emitDir(c.x + (Math.random() - 0.5) * 0.6, gy + 0.3 + Math.random() * 1.6, c.z + (Math.random() - 0.5) * 0.6, -c.vx * 0.15, 0.5 + Math.random() * 0.6, -c.vz * 0.15, this.hex, 0.22, 0.35, 2);
    }
    for (const lk of this.looks) {
      if (lk.out < 0) { if (!lk.id) lk.g.root.visible = false; continue; }
      if (lk.out === 0) continue;
      lk.out = Math.max(0, lk.out - dt);
      lk.alpha.value = (lk.out / FADE_OUT) ** 1.5; lk.flash.value = 0.5 * (1 - lk.out / FADE_OUT);
      lk.g.root.position.y += dt * 0.8;
      if (lk.out === 0) { lk.g.root.visible = false; lk.id = 0; lk.out = -1; }
    }
    // bolts
    const camPos = cam.position; let n = 0;
    for (const b of sim.splitShots) {
      if (!b.alive || n >= SHOTS) continue;
      let r = this.recs.get(b.id);
      if (!r) { r = { ox: 0, oy: this.groundAt(b.x, b.z) + 1.25, oz: 0, age: EASE_T }; this.recs.set(b.id, r); }
      r.age += dt;
      const e = Math.max(0, 1 - r.age / EASE_T), k = e * e, x = b.x + r.ox * k, z = b.z + r.oz * k, y = r.oy;
      const sp = Math.hypot(b.vx, b.vz) || 1;
      _d.set(b.vx / sp, 0, b.vz / sp);
      _side.set(camPos.x - x, camPos.y - y, camPos.z - z).cross(_d).normalize(); _n.copy(_d).cross(_side);
      const L = b.evo ? 1.05 : 0.9, W = b.evo ? 0.36 : 0.3;
      _m.makeBasis(_dl.copy(_d).multiplyScalar(L), _side.multiplyScalar(W), _n); _m.setPosition(x, y, z);
      this.streak.setMatrixAt(n, _m);
      _p.set(x - _d.x * L * 0.2, y, z - _d.z * L * 0.2); _s.setScalar(b.evo ? 0.75 : 0.6);
      this.halo.setMatrixAt(n, _m.compose(_p, cam.quaternion, _s));
      if (Math.random() < 0.5) this.fx.particles.emitDir(x - _d.x * L * 0.6, y, z - _d.z * L * 0.6, -_d.x * 2, 0.3, -_d.z * 2, this.hex, 0.2, 0.16, 2.2); // a short sparkle tail
      n++;
    }
    this.streak.count = n; this.halo.count = n;
    this.streak.instanceMatrix.needsUpdate = true; this.halo.instanceMatrix.needsUpdate = true;
    if (this.recs.size > SHOTS * 2) { for (const id of this.recs.keys()) if (!sim.splitShots.some((q) => q.alive && q.id === id)) this.recs.delete(id); }
  }

  private burst(x: number, z: number): void {
    const y = this.groundAt(x, z);
    this.fx.stars.add(x, y + 1.1, z, 2.2, 0.16, this.hex, 2.4);
    this.fx.rings.add(x, y + 0.1, z, 2.4, 0.35, this.hex, 1.6, 1);
    for (let k = 0; k < 14; k++) { const a = (k / 14) * 6.283; this.fx.particles.emitDir(x, y + 0.4 + Math.random() * 1.4, z, Math.cos(a) * 4, 1 + Math.random() * 2, Math.sin(a) * 4, k % 3 ? this.hex : 0xffffff, 0.25, 0.4, 2.4); }
  }
  private dissolve(lk: Look): void {
    const y = this.groundAt(lk.x, lk.z);
    for (let k = 0; k < 18; k++) this.fx.particles.emitDir(lk.x + (Math.random() - 0.5) * 0.9, y + 0.2 + Math.random() * 1.9, lk.z + (Math.random() - 0.5) * 0.9, (Math.random() - 0.5) * 1.5, 1.5 + Math.random() * 2.5, (Math.random() - 0.5) * 1.5, k % 4 ? this.hex : 0xffffff, 0.2, 0.6, 2.2);
  }

  /** Ground glow: a soft pool of the copy's light under each copy and a small one under each bolt */
  glowPools(g: GroundGlow, sim: Sim): void {
    for (const c of sim.splits) if (c.alive) g.blob(c.x, this.groundAt(c.x, c.z) + 1, c.z, 1.3, this.hex, 0.45);
    let n = 0;
    for (const b of sim.splitShots) if (b.alive && n++ < 32) g.blob(b.x, b.y, b.z, 0.6, this.hex, 0.35);
  }

  clear(): void { this.recs.clear(); this.streak.count = 0; this.halo.count = 0; for (const lk of this.looks) { lk.g.root.visible = false; lk.id = 0; lk.out = -1; } }
}
